import Space from "antd/es/space";
import FAIcon from "../../../common/FAIcon/FAIcon";
import { defineMessage, defineMessages, FormattedMessage, useIntl } from "react-intl";
import Row from "antd/es/row";
import Col from "antd/es/col";
import FloatingDatePickerInput from "../../../common/Inputs/Floating/FloatingDatePicker";
import FloatingSelectInput from "../../../common/Inputs/Floating/FloatingSelect";
import Button from "antd/es/button";
import UploadButton from "../../../common/UploadButton/UploadButton";
import FloatingTextAreaInput from "../../../common/Inputs/Floating/FloatingTextArea";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { showNotification } from "../../../../utils/utils";
import Network from "../../../../utils/Network";
import { ConvertDateToNetworkDate, ConvertNetworkSunkhronosCompany, ConvertSunkhronosCompany } from "../../../../utils/converters";
import { SunkhronosCompany } from "../../../../utils/types/applications/sunkhronosTypes";
import message from "antd/es/message";
import Upload from "antd/es/upload";
import { cloneDeep } from "lodash";
import { SunkhronosCompanyBillingFrequencies } from "../../utils/constants";
import { ContractTypeModeType } from "../../utils/types";
import dayjs, { Dayjs } from "dayjs";
import CircleButton from "../../../common/CircleButton/CircleButton";

interface Props {
    selectedCompany?: SunkhronosCompany;
    selectedDate: Dayjs;
    cancelText: ReactNode;
    onCancel?: (companies?: SunkhronosCompany) => void;
}

const SunkhronosCompanyBillingFrequenciesMessages = defineMessages<ContractTypeModeType>({
    MONTHLY: { defaultMessage: "Monthly (x1 month)" },
    QUARTERLY: { defaultMessage: "Quarterly (x3 months)" },
    HALF_YEARLY: { defaultMessage: "Half-yearly (x6 months)" },
    YEARLY: { defaultMessage: "Yearly (x12 months)" },
});

const EditCompanyContent = (props: Props) => {
    const intl = useIntl();

    const [contract, setContract] = useState<File | undefined>();
    const [offer, setOffer] = useState<File | undefined>();
    const [selectedCompany, setSelectedCompany] = useState<SunkhronosCompany | undefined>(cloneDeep(props.selectedCompany));
    const [loading, setLoading] = useState(false);
    const [send, setSend] = useState<1 | 2 | 3>(1);
    const [sendOffer, setSendOffer] = useState<1 | 2 | 3>(1);
    const [deleteContract, setDeleteContract] = useState(false);
    const [deleteOffer, setDeleteOffer] = useState(false);


    useEffect(() => {
        if (!props.selectedCompany?.id) return;

        Network.fetchContract(props.selectedCompany.id)
            .then((blobResponse) => {
                const contentDisposition = blobResponse.headers['content-disposition'];

                let filename = 'contract.pdf';
                const match = /filename="(.+)"/.exec(contentDisposition);
                if (match) {
                    filename = match[1];
                }

                const blob = blobResponse.data;
                const file = new File([blob], filename, { type: blob.type });

                setContract(file);
                setSend(3);
            })
            .catch((error) => {
                console.error("Error fetching contract:", error);
            });

        Network.fetchOffer(props.selectedCompany.id)
            .then((blobResponse) => {
                const contentDisposition = blobResponse.headers['content-disposition'];

                let filename = 'offer.pdf';
                const match = /filename="(.+)"/.exec(contentDisposition);
                if (match) {
                    filename = match[1];
                }

                const blob = blobResponse.data;
                const file = new File([blob], filename, { type: blob.type });

                setOffer(file);
                setSendOffer(3);
            })
            .catch((error) => {
                console.error("Error fetching offer:", error);
            });
    }, [props.selectedCompany?.id, props.selectedCompany?.name]);

    const beforeUpload = useCallback((file: File) => {
        if (file.type !== "application/pdf") {
            message.error(intl.formatMessage({ defaultMessage: 'You can only load PDF file' }));
            return Upload.LIST_IGNORE;
        }
        const isLt5M = file.size / 1024 / 1024 < 100;
        if (!isLt5M) {
            message.error(intl.formatMessage({ defaultMessage: 'File must be smaller than 100MB!' }));
            return Upload.LIST_IGNORE;
        }

        setContract(file);
        setSend(2);
        return false;
    }, [intl]);

    const beforeUploadOffer = useCallback((file: File) => {
        if (file.type !== "application/pdf") {
            message.error(intl.formatMessage({ defaultMessage: 'You can only load PDF file' }));
            return Upload.LIST_IGNORE;
        }
        const isLt5M = file.size / 1024 / 1024 < 100;
        if (!isLt5M) {
            message.error(intl.formatMessage({ defaultMessage: 'File must be smaller than 100MB!' }));
            return Upload.LIST_IGNORE;
        }

        setOffer(file);
        setSendOffer(2);
        return false;
    }, [intl]);


    useEffect(() => {
        if (props.selectedCompany !== undefined)
            setSelectedCompany(props.selectedCompany);
    }, [props.selectedCompany]);

    const selectedCompanyLicenceErrors = useMemo(() => {
        return {
            startDateError: selectedCompany ? selectedCompany.licenceFrom?.isAfter(selectedCompany.licenceTo) : false,
            startDateMissing: selectedCompany ? selectedCompany.licenceFrom === undefined && selectedCompany.licenceTo !== undefined : false
        };
    }, [selectedCompany]);

    const selectedCompanyContractErrors = useMemo(() => {
        return {
            startDateError: selectedCompany ? selectedCompany.contractFrom?.isAfter(selectedCompany.contractTo) : false,
            startDateMissing: selectedCompany ? selectedCompany.contractFrom === undefined && selectedCompany.contractTo !== undefined : false
        };
    }, [selectedCompany]);

    const reset = useCallback((company?: SunkhronosCompany) => {
        setContract(undefined);
        setSend(1);
        setDeleteContract(false);
        setOffer(undefined);
        setSendOffer(1);
        setDeleteOffer(false);
        if (company !== undefined)
            setSelectedCompany(cloneDeep(company));
        else
            setSelectedCompany(cloneDeep(props.selectedCompany));
        props.onCancel?.(company);
    }, [props]);

    const handleSave = useCallback(() => {
        if (!selectedCompany) {
            showNotification(intl.formatMessage({ defaultMessage: 'Selected company is undefined' }), 'error');
            return;
        }

        const formData = new FormData();
        if (contract && send === 2) {
            // Only send the file if it's a new upload
            formData.append("file", contract);
        }

        if (offer && sendOffer === 2) {
            // Only send the offer file if it's a new upload
            formData.append("offer_file", offer);
        }

        // Create a copy of the company data to add the delete flags
        const companyData = ConvertSunkhronosCompany(selectedCompany);

        // Add the delete_contract flag if needed
        if (deleteContract) {
            companyData.delete_contract = true;
        }

        // Add the delete_offer flag if needed
        if (deleteOffer) {
            companyData.delete_offer = true;
        }

        formData.append("company", JSON.stringify(companyData));

        setLoading(true);
        Network.updateSunkhronosCompany(selectedCompany.id, formData, ConvertDateToNetworkDate(props.selectedDate)).then(
            (res) => {
                const updatedCompany = ConvertNetworkSunkhronosCompany(res.data);

                // Reset flags
                setDeleteContract(false);
                setDeleteOffer(false);

                // Keep or reload contract file
                if (deleteContract) {
                    setContract(undefined);
                    setSend(1);
                } else if (send === 2) {
                    // Keep the uploaded file, mark as saved
                    setSend(3);
                } else if (updatedCompany.hasContract) {
                    // Reload contract from server
                    Network.fetchContract(selectedCompany.id)
                        .then((blobResponse) => {
                            const contentDisposition = blobResponse.headers['content-disposition'];
                            let filename = 'contract.pdf';
                            const match = /filename="(.+)"/.exec(contentDisposition);
                            if (match) filename = match[1];
                            const blob = blobResponse.data;
                            const file = new File([blob], filename, { type: blob.type });
                            setContract(file);
                            setSend(3);
                        })
                        .catch(() => {
                            setContract(undefined);
                            setSend(1);
                        });
                }

                // Keep or reload offer file
                if (deleteOffer) {
                    setOffer(undefined);
                    setSendOffer(1);
                } else if (sendOffer === 2) {
                    // Keep the uploaded file, mark as saved
                    setSendOffer(3);
                } else if (updatedCompany.hasOffer) {
                    // Reload offer from server
                    Network.fetchOffer(selectedCompany.id)
                        .then((blobResponse) => {
                            const contentDisposition = blobResponse.headers['content-disposition'];
                            let filename = 'offer.pdf';
                            const match = /filename="(.+)"/.exec(contentDisposition);
                            if (match) filename = match[1];
                            const blob = blobResponse.data;
                            const file = new File([blob], filename, { type: blob.type });
                            setOffer(file);
                            setSendOffer(3);
                        })
                        .catch(() => {
                            setOffer(undefined);
                            setSendOffer(1);
                        });
                }

                setSelectedCompany(cloneDeep(updatedCompany));
                props.onCancel?.(updatedCompany);
                setLoading(false);
            },
            () => {
                showNotification(intl.formatMessage({ defaultMessage: 'An error occurred while updating the company' }), 'error');
                setLoading(false);
            }
        );
    }, [selectedCompany, contract, send, offer, sendOffer, deleteContract, deleteOffer, intl, props]);

    const openContract = useCallback((file: File) => {
        const url = URL.createObjectURL(file);
        window.open(url);
    }, []);


    return (
        <div className='__edit-company-container'>
            <div className='__edit-company-content'>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    <h3 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <FAIcon prefix="fad" name="file-certificate" />
                        <FormattedMessage defaultMessage="Licence" />
                    </h3>
                    <Row gutter={[10, 30]} style={{ marginTop: '1rem' }}>
                        <Col xs={{ span: 12 }}>
                            <FloatingDatePickerInput
                                label={<FormattedMessage defaultMessage="From date" />}
                                value={selectedCompany?.licenceFrom}
                                allowClear={false}
                                disabled={loading}
                                error={selectedCompanyLicenceErrors.startDateError || selectedCompanyLicenceErrors.startDateMissing}
                                errorText={selectedCompanyLicenceErrors.startDateError
                                    ? intl.formatMessage({ defaultMessage: "The start date must be before the end date" })
                                    : selectedCompanyLicenceErrors.startDateMissing
                                        ? intl.formatMessage({ defaultMessage: "This field is required" })
                                        : intl.formatMessage({ defaultMessage: "Unknown error" })}
                                onChange={(e) => {
                                    const tmpSelectedCompany = cloneDeep(selectedCompany);
                                    if (tmpSelectedCompany) tmpSelectedCompany.licenceFrom = e;
                                    setSelectedCompany(tmpSelectedCompany);
                                }}
                            />
                        </Col>
                        <Col xs={{ span: 12 }}>
                            <FloatingDatePickerInput
                                label={<FormattedMessage defaultMessage="To date" />}
                                value={selectedCompany?.licenceTo}
                                allowClear={false}
                                disabled={loading}
                                onChange={(e) => {
                                    const tmpSelectedCompany = cloneDeep(selectedCompany);
                                    if (tmpSelectedCompany) tmpSelectedCompany.licenceTo = e;
                                    setSelectedCompany(tmpSelectedCompany);
                                }}
                            />
                        </Col>
                    </Row>

                    {/* Invoice Section */}
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <h3 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <FAIcon prefix="fad" name="file-invoice" />
                            <FormattedMessage defaultMessage="Invoice" />
                        </h3>
                        <Row gutter={[10, 10]} style={{ marginTop: '1rem' }}>
                            <Col xs={{ span: 12 }}>
                                <FloatingSelectInput
                                    label={<FormattedMessage defaultMessage="Frequency" />}
                                    value={selectedCompany?.billingFrequency}
                                    allowClear
                                    options={Object.keys(SunkhronosCompanyBillingFrequencies).map(i => ({
                                        value: SunkhronosCompanyBillingFrequencies[i as ContractTypeModeType],
                                        label: intl.formatMessage(SunkhronosCompanyBillingFrequenciesMessages[i as ContractTypeModeType])
                                    }))}
                                    onChange={(e: number) => {
                                        const tmpSelectedCompany = cloneDeep(selectedCompany);
                                        if (tmpSelectedCompany) tmpSelectedCompany.billingFrequency = e;
                                        setSelectedCompany(tmpSelectedCompany);
                                    }}
                                />
                            </Col>
                            <Col xs={{ span: 12 }}>
                                <FloatingDatePickerInput
                                    label={<FormattedMessage defaultMessage="Last date" />}
                                    value={selectedCompany?.lastInvoice}
                                    allowClear={false}
                                    disabled={loading}
                                    onChange={(e) => {
                                        const tmpSelectedCompany = cloneDeep(selectedCompany);
                                        if (tmpSelectedCompany) tmpSelectedCompany.lastInvoice = e;
                                        setSelectedCompany(tmpSelectedCompany);
                                    }}
                                />
                            </Col>
                        </Row>
                    </Space>

                    {/* Offer Section */}
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <h3 className="centerHeight" style={{ gap: "8px" }}>
                            <FAIcon prefix="fad" name="file-contract" />
                            <FormattedMessage defaultMessage="Offer" />
                            <CircleButton
                                icon={<FAIcon prefix='fad' name='signature' />}
                                title={intl.formatMessage({ defaultMessage: 'Offer signed' })}
                                type={selectedCompany?.offerSigned ? "primary" : "default"}
                                onClick={() => {
                                    const tmpSelectedCompany = cloneDeep(selectedCompany);
                                    if (tmpSelectedCompany) tmpSelectedCompany.offerSigned = !tmpSelectedCompany.offerSigned;
                                    setSelectedCompany(tmpSelectedCompany);
                                }}
                            />
                            <CircleButton
                                icon={<FAIcon prefix='fad' name='eye' />}
                                title={intl.formatMessage({ defaultMessage: 'View offer' })}
                                disabled={offer === undefined}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (offer) {
                                        openContract(offer);
                                    }
                                }}
                            />
                            <CircleButton
                                icon={<FAIcon prefix='fad' name='trash' />}
                                title={intl.formatMessage({ defaultMessage: 'Delete offer' })}
                                disabled={offer === undefined}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOffer(undefined);
                                    setDeleteOffer(true);
                                }}
                            />
                        </h3>
                        <Row className="flex-between" gutter={[10, 10]}>
                            <Col className='inline centerHeight' xs={{ span: 24 }} style={{ paddingTop: '1rem' }} >
                                <UploadButton text={defineMessage({ defaultMessage: "Load Offer" })} file={offer} beforeUpload={beforeUploadOffer} />
                            </Col>
                        </Row>
                    </Space>

                    {/* Contract Section */}
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <h3 className="centerHeight" style={{ gap: "8px" }}>
                            <FAIcon prefix="fad" name="file-contract" />
                            <FormattedMessage defaultMessage="Contract" />
                            <CircleButton
                                icon={<FAIcon prefix='fad' name='ban' />}
                                title={intl.formatMessage({ defaultMessage: 'Contract terminated' })}
                                type={selectedCompany?.contractTerminated ? "primary" : "default"}
                                onClick={() => {
                                    const tmpSelectedCompany = cloneDeep(selectedCompany);
                                    if (tmpSelectedCompany) tmpSelectedCompany.contractTerminated = !tmpSelectedCompany.contractTerminated;
                                    setSelectedCompany(tmpSelectedCompany);
                                }}
                            />
                            <CircleButton
                                icon={<FAIcon prefix='fad' name='eye' />}
                                title={intl.formatMessage({ defaultMessage: 'View contract' })}
                                disabled={contract === undefined}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (contract) {
                                        openContract(contract);
                                    }
                                }}
                            />
                            <CircleButton
                                icon={<FAIcon prefix='fad' name='trash' />}
                                title={intl.formatMessage({ defaultMessage: 'Delete contract' })}
                                disabled={contract === undefined}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setContract(undefined);
                                    setDeleteContract(true);
                                }}
                            />

                        </h3>
                        <Row className="flex-between" gutter={[10, 10]}>
                            <Col className='inline centerHeight' xs={{ span: 24 }} style={{ paddingTop: '1rem' }} >
                                <UploadButton text={defineMessage({ defaultMessage: "Load Contract" })} file={contract} beforeUpload={beforeUpload} />
                                <Col className="full-width">
                                    <Row gutter={[0, 12]}>
                                        <Col xs={{ span: 24 }}>
                                            <FloatingDatePickerInput
                                                className="full-width"
                                                label={<FormattedMessage defaultMessage="From date" />}
                                                value={selectedCompany?.contractFrom}
                                                allowClear={!selectedCompany?.contractTo}
                                                disabled={loading}
                                                error={selectedCompanyContractErrors.startDateError || selectedCompanyContractErrors.startDateMissing}
                                                errorText={selectedCompanyContractErrors.startDateError
                                                    ? intl.formatMessage({ defaultMessage: "The start date must be before the end date" })
                                                    : selectedCompanyContractErrors.startDateMissing
                                                        ? intl.formatMessage({ defaultMessage: "This field is required" })
                                                        : intl.formatMessage({ defaultMessage: "Unknown error" })}
                                                onChange={(e) => {
                                                    const tmpSelectedCompany = cloneDeep(selectedCompany);
                                                    if (tmpSelectedCompany) tmpSelectedCompany.contractFrom = e;
                                                    setSelectedCompany(tmpSelectedCompany);
                                                }}
                                            />
                                        </Col>
                                        <Col xs={{ span: 24 }}>
                                            <FloatingDatePickerInput
                                                className="full-width"
                                                label={<FormattedMessage defaultMessage="To date" />}
                                                value={selectedCompany?.contractTo}
                                                disabled={loading || !selectedCompany?.contractFrom}
                                                onChange={(e) => {
                                                    const tmpSelectedCompany = cloneDeep(selectedCompany);
                                                    if (tmpSelectedCompany) tmpSelectedCompany.contractTo = e;
                                                    setSelectedCompany(tmpSelectedCompany);
                                                }}
                                            />
                                        </Col>
                                        <Col xs={{ span: 24 }}>
                                            <FloatingDatePickerInput
                                                className="full-width"
                                                label={<FormattedMessage defaultMessage="Trial period" />}
                                                value={selectedCompany?.contractTrialPeriod}
                                                disabled={loading}
                                                onChange={(e) => {
                                                    const tmpSelectedCompany = cloneDeep(selectedCompany);
                                                    if (tmpSelectedCompany) tmpSelectedCompany.contractTrialPeriod = e;
                                                    setSelectedCompany(tmpSelectedCompany);
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Col>
                        </Row>
                    </Space>

                    {/* Extra Section (Fieldset conservé) */}
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <h3 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <FAIcon prefix="fad" name="pen-field" />
                            <FormattedMessage defaultMessage="Extra" />
                        </h3>
                        <Row gutter={[10, 10]} style={{ marginTop: '1rem' }}>
                            <Col xs={{ span: 24 }}>
                                <FloatingDatePickerInput
                                    label={<FormattedMessage defaultMessage="Expiration date" />}
                                    value={selectedCompany?.expiration ? dayjs(selectedCompany?.expiration) : undefined}
                                    disabled={loading}
                                    onChange={(e) => {
                                        const tmpSelectedCompany = cloneDeep(selectedCompany);
                                        if (tmpSelectedCompany) tmpSelectedCompany.expiration = e;
                                        setSelectedCompany(tmpSelectedCompany);
                                    }}
                                />
                            </Col>
                            <Col xs={{ span: 24 }} style={{ marginTop: "1em" }}>
                                <FloatingTextAreaInput
                                    label={<FormattedMessage defaultMessage="Comments" />}
                                    rows={6}
                                    value={selectedCompany?.comment}
                                    disabled={loading}
                                    onChange={(e) => {
                                        const tmpSelectedCompany = cloneDeep(selectedCompany);
                                        if (tmpSelectedCompany) tmpSelectedCompany.comment = e.target.value;
                                        setSelectedCompany(tmpSelectedCompany);
                                    }}
                                />
                            </Col>
                        </Row>
                    </Space>
                </Space>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '1rem' }}>
                    <div className='__edit-company-counts'>
                        <FAIcon prefix='fad' name='user' /><span className='__edit-company-count-value'> {props.selectedCompany?.admins}</span> <span className='__edit-company-count-label'><FormattedMessage defaultMessage={'Admins'} /></span>
                    </div>
                    <div className='__edit-company-counts'>
                        <FAIcon prefix='fad' name='user-shield' /><span className='__edit-company-count-value'> {props.selectedCompany?.groupAdmins}</span> <span className='__edit-company-count-label'><FormattedMessage defaultMessage={'Group Admins'} /></span>
                    </div>
                </div>
            </div>
            <div className='__edit-company-footer'>
                <Button onClick={() => reset()} disabled={loading}>{props.cancelText}</Button>
                <Button type='primary' onClick={handleSave} loading={loading}>{<FormattedMessage defaultMessage={'Save'} />}</Button>
            </div>
        </div>
    );
};

export default EditCompanyContent;